import cn from 'classnames';
import { PerformanceOrderReviewResultFragment } from 'src/gqlReactTypings.generated.d';
import { parseDate } from 'src/shared/utils/DateTimeUtilities';
import { match, P } from 'ts-pattern';
import { Icon, IconColor } from 'web-common/src/shared/design-system/components/Icon';

interface DishModalReviewCardProps {
  review: PerformanceOrderReviewResultFragment;
  foodItemId: number;
}

/**
 * Get thumb icon name for the specific food item's rating in this review
 * 0 = thumbsDown, 100 = thumbsUp, 50 = thumbsSide, null = no icon
 */
const getReviewThumbIconName = (
  review: PerformanceOrderReviewResultFragment,
  foodItemId: number
): 'thumbsUp' | 'thumbsDown' | 'thumbsSide' | null => {
  const dish = review.dishes.find((d) => d?.foodItemId === foodItemId);
  const ratingPercent = dish?.rating?.ratingPercent;

  return match(ratingPercent)
    .with(P.nullish, () => null)
    .with(100, () => 'thumbsUp' as const)
    .with(50, () => 'thumbsSide' as const)
    .with(0, () => 'thumbsDown' as const)
    .otherwise(() => null);
};

export const DishModalReviewCard: React.FC<DishModalReviewCardProps> = ({ review, foodItemId }) => {
  const date = parseDate(review.createdAt);
  const thumbIconName = getReviewThumbIconName(review, foodItemId);
  const formattedDate = date.toFormat('MMM d');

  return (
    <div className='rounded-lg bg-milk p-4'>
      {/* Thumb icon - only show if rating exists */}
      {thumbIconName && <Icon name={thumbIconName} size={20} color={IconColor.EGGPLANT_500} />}

      {/* Review text */}
      <p className={cn('text-body-400 text-eggplant-500', { 'my-3': thumbIconName, 'mb-3': !thumbIconName })}>
        {review.comment}
      </p>

      {/* Name and date */}
      <div className='flex items-center justify-between'>
        <span className='text-caption-600 text-eggplant'>-{review.user.publicName}</span>
        <span className='text-label-400 text-eggplant-300'>{formattedDate}</span>
      </div>
    </div>
  );
};
